"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import Footer from "@/components/Footer";
import { BrandMark } from "@/components/BrandMark";

type Lang = "en" | "es";

const WEB3FORMS_KEY = "4644d3d5-8f48-4352-8b82-23bb8e525cf3";
const SUPPORT_EMAIL = "support@draffte.com";

const T = {
  title: { en: "Contact us", es: "Contáctanos" },
  sub: {
    en: "Questions, partnerships, press or support — send us a message and we'll get back to you by email.",
    es: "Preguntas, alianzas, prensa o soporte — envíanos un mensaje y te responderemos por correo.",
  },
  name: { en: "Name", es: "Nombre" },
  email: { en: "Email", es: "Correo electrónico" },
  subject: { en: "Subject", es: "Asunto" },
  message: { en: "Message", es: "Mensaje" },
  send: { en: "Send message", es: "Enviar mensaje" },
  sending: { en: "Sending…", es: "Enviando…" },
  successTitle: { en: "Message sent", es: "Mensaje enviado" },
  successBody: {
    en: "Thanks for reaching out — we'll reply to your email soon.",
    es: "Gracias por escribirnos — te responderemos a tu correo pronto.",
  },
  error: {
    en: "Something went wrong. Please email us directly at",
    es: "Algo salió mal. Escríbenos directamente a",
  },
  backToSite: { en: "← Back to site", es: "← Volver al sitio" },
  orEmail: { en: "Prefer email? Write to", es: "¿Prefieres correo? Escribe a" },
} satisfies Record<string, { en: string; es: string }>;

export default function ContactPage() {
  const [lang, setLang] = useState<Lang>("en");
  useEffect(() => {
    document.title = "Contact · Draffte Passport";
    const s = localStorage.getItem("draffte-lang");
    if (s === "es" || s === "en") setLang(s);
  }, []);
  const changeLang = (next: Lang) => {
    setLang(next);
    try {
      localStorage.setItem("draffte-lang", next);
    } catch {
      /* ignore */
    }
  };

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [honey, setHoney] = useState(""); // spam honeypot — must stay empty
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (status === "submitting" || status === "success") return;
    if (honey) {
      setStatus("success"); // silently drop bots
      return;
    }
    setStatus("submitting");
    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          access_key: WEB3FORMS_KEY,
          from_name: "Draffte Passport",
          subject: subject.trim() ? `[Draffte] ${subject.trim()}` : "New message from draffte.com/contact",
          name: name.trim(),
          email: email.trim(),
          message: message.trim(),
        }),
      });
      const data = await res.json().catch(() => ({ success: false }));
      setStatus(res.ok && data.success ? "success" : "error");
    } catch {
      setStatus("error");
    }
  }

  const inputCls =
    "w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-white placeholder-zinc-600 outline-none transition-colors focus:border-violet-500/60 focus:bg-white/[0.06]";
  const labelCls = "block text-sm font-medium text-zinc-400 mb-1.5";

  return (
    <div className="min-h-screen bg-[#050508] text-zinc-100">
      <header className="sticky top-0 z-20 border-b border-white/10 bg-[#050508]/80 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4 sm:px-8">
          <Link href="/" className="flex items-center gap-2.5" aria-label="Draffte">
            <BrandMark className="h-8 w-8 rounded-lg shadow-[0_0_20px_rgba(139,92,246,0.25)]" />
            <span className="font-display text-lg font-semibold tracking-tight text-white">Draffte</span>
          </Link>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-0.5 rounded-full border border-white/10 bg-white/5 p-0.5">
              <button
                type="button"
                onClick={() => changeLang("en")}
                aria-pressed={lang === "en"}
                className={`rounded-full px-2.5 py-1 text-xs font-semibold transition-colors ${
                  lang === "en" ? "bg-violet-500/90 text-white" : "text-zinc-400 hover:text-white"
                }`}
              >
                EN
              </button>
              <button
                type="button"
                onClick={() => changeLang("es")}
                aria-pressed={lang === "es"}
                className={`rounded-full px-2.5 py-1 text-xs font-semibold transition-colors ${
                  lang === "es" ? "bg-violet-500/90 text-white" : "text-zinc-400 hover:text-white"
                }`}
              >
                ES
              </button>
            </div>
            <Link href="/" className="hidden text-sm text-zinc-400 hover:text-white sm:block">
              {T.backToSite[lang]}
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-xl px-6 py-16 sm:px-8 sm:py-24">
        <h1 className="font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">{T.title[lang]}</h1>
        <p className="mt-3 text-base leading-relaxed text-zinc-400">{T.sub[lang]}</p>

        {status === "success" ? (
          <div className="mt-10 rounded-2xl border border-emerald-500/30 bg-emerald-500/[0.08] px-6 py-10 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/15 text-2xl text-emerald-400">
              ✓
            </div>
            <h2 className="mt-4 font-display text-xl font-semibold text-white">{T.successTitle[lang]}</h2>
            <p className="mt-2 text-sm text-zinc-400">{T.successBody[lang]}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mt-10 flex flex-col gap-5">
            {/* honeypot — visually hidden, bots fill it */}
            <input
              type="text"
              name="botcheck"
              value={honey}
              onChange={(e) => setHoney(e.target.value)}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              className="hidden"
            />
            <div>
              <label htmlFor="name" className={labelCls}>
                {T.name[lang]} <span className="text-rose-400">*</span>
              </label>
              <input id="name" required value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label htmlFor="email" className={labelCls}>
                {T.email[lang]} <span className="text-rose-400">*</span>
              </label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputCls}
                placeholder="you@email.com"
              />
            </div>
            <div>
              <label htmlFor="subject" className={labelCls}>
                {T.subject[lang]}
              </label>
              <input id="subject" value={subject} onChange={(e) => setSubject(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label htmlFor="message" className={labelCls}>
                {T.message[lang]} <span className="text-rose-400">*</span>
              </label>
              <textarea
                id="message"
                required
                rows={6}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className={`${inputCls} resize-y`}
              />
            </div>
            <button
              type="submit"
              disabled={status === "submitting"}
              className="mt-2 inline-flex items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-violet-700 px-6 py-3.5 text-sm font-semibold text-white shadow-[0_0_30px_rgba(139,92,246,0.35)] transition-opacity hover:opacity-95 disabled:opacity-60"
            >
              {status === "submitting" ? T.sending[lang] : T.send[lang]}
            </button>
            {status === "error" && (
              <p className="text-sm text-rose-300">
                {T.error[lang]}{" "}
                <a href={`mailto:${SUPPORT_EMAIL}`} className="underline hover:text-rose-200">
                  {SUPPORT_EMAIL}
                </a>
              </p>
            )}
            <p className="mt-1 text-center text-xs text-zinc-600">
              {T.orEmail[lang]}{" "}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="text-zinc-400 hover:text-white">
                {SUPPORT_EMAIL}
              </a>
            </p>
          </form>
        )}
      </main>

      <Footer lang={lang} />
    </div>
  );
}
